import type { Tracker } from "@infinitaslearning/module-data-analyticstracker";
import { useCallback, useEffect, useRef } from "react";
import { useAnalyticsTracker } from "./analyticsContext";

/**
 * Safely extracts a string message from an unknown error value.
 * Handles Error objects, plain strings, and anything else.
 */
export function toErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return String(error);
}

/**
 * Fires a page-view event once per unique `key` (or on mount when `key` is
 * omitted). Use `key` for components that stay mounted across navigation
 * (e.g. a drawer that opens different titles) so each navigation fires a
 * fresh page view.
 */
export function usePageTracking(event: Parameters<Tracker["page"]>[0], key?: string) {
  const tracker = useAnalyticsTracker();
  const hasTracked = useRef(false);
  const trackedKey = useRef<string | undefined>(undefined);

  useEffect(() => {
    const keyChanged = key !== undefined && key !== trackedKey.current;
    if (tracker && (!hasTracked.current || keyChanged)) {
      tracker.page(event);
      hasTracked.current = true;
      trackedKey.current = key;
    }
  }, [tracker, event, key]);
}

export function useFrameOpenedTracking() {
  const tracker = useAnalyticsTracker();
  return useCallback(
    (params: Parameters<Tracker["frameOpened"]>[0]) => {
      tracker?.frameOpened(params);
    },
    [tracker],
  );
}

export function useFrameClosedTracking() {
  const tracker = useAnalyticsTracker();
  return useCallback(
    (params: Parameters<Tracker["frameClosed"]>[0]) => {
      tracker?.frameClosed(params);
    },
    [tracker],
  );
}

export function useElementClickedTracking() {
  const tracker = useAnalyticsTracker();
  return useCallback(
    (params: Parameters<Tracker["elementClicked"]>[0]) => {
      tracker?.elementClicked(params);
    },
    [tracker],
  );
}

export function useFileUploadedTracking() {
  const tracker = useAnalyticsTracker();
  return useCallback(
    (params: Parameters<Tracker["fileUploaded"]>[0]) => {
      tracker?.fileUploaded(params);
    },
    [tracker],
  );
}

export function useErrorTracking() {
  const tracker = useAnalyticsTracker();
  return useCallback(
    (params: Parameters<Tracker["errorCapture"]>[0]) => {
      tracker?.errorCapture(params);
    },
    [tracker],
  );
}
